import React, { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  FileSpreadsheet,
  BarChart3,
  Zap,
  Repeat2,
  MapPin,
  Tags,
  Images,
  Gauge,
  Rocket,
  Database,
  Brain,
  Wrench,
  GitBranch,
  ArrowLeftRight,
  Building2,
} from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  DatasetSubView,
  InferenceSubView,
  ModelSubView,
  DashboardSubView,
  setActiveDatasetSubView,
  setActiveInferenceSubView,
  setActiveModelSubView,
  setActiveDashboardSubView,
  selectActiveDatasetSubView,
  selectActiveInferenceSubView,
  selectActiveModelSubView,
  selectActiveDashboardSubView,
  DatasetSubNavItems,
  InferenceSubNavItems,
  ModelSubNavItems,
  selectSelectedProjectId,
  DashboardSubNavItems,
} from '../../../store/slices/projectSlice'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useFetchModelsQuery, useGetModelQuery } from '../../../services/modelsApi'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'

const NavContainer = styled.div<{ $isCollapsed: boolean }>`
  position: fixed;
  left: 0;
  top: 6vh;
  background-color: #2a2a2a;
  height: 94vh;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${(props) => (props.$isCollapsed ? '4.5rem' : '20rem')};
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 12px rgba(0, 0, 0, 0.3), inset -1px 0 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  z-index: 9999;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.05) 0%,
      rgba(255, 255, 255, 0.02) 100%
    );
    pointer-events: none;
  }
`

const ToggleButton = styled.button`
  position: absolute;
  right: -14px;
  top: 96px;
  width: 28px;
  height: 28px;
  background: #800085;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(128, 0, 133, 0.4);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    background: #9a009f;
    box-shadow: 0 3px 10px rgba(128, 0, 133, 0.6);
    border-color: rgba(255, 255, 255, 0.4);
  }

  svg {
    width: 20px;
    height: 20px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
  }
`

const NavContent = styled.div`
  padding: 8rem 0.5rem 0.5rem 0.5rem;
  color: white;
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`

const Divider = styled.div<{ $isCollapsed: boolean }>`
  height: 2px;
  background: rgba(255, 255, 255, 0.7);
  margin: 0.75rem ${(props) => (props.$isCollapsed ? '0.5rem' : '1rem')};
  opacity: 1;
  border-bottom: 1px solid rgba(0, 0, 0, 0.5);
  transition: margin 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const NavSection = styled.div`
  margin-bottom: 1rem;

  &:first-child {
    margin-top: 1.5rem;
  }
`

const NavItem = styled.button<{
  $isActive: boolean
  $isCollapsed: boolean
  $isDisabled?: boolean
}>`
  width: 100%;
  padding: 0;
  padding-left: 0.75rem;
  margin: 0;
  background-color: ${(props) => (props.$isActive ? 'rgba(128, 0, 133, 0.15)' : 'transparent')};
  border: none;
  border-radius: 8px;
  color: ${(props) => (props.$isDisabled ? '#666' : 'white')};
  text-align: left;
  cursor: ${(props) => (props.$isDisabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isCollapsed ? 'center' : 'flex-start')};
  gap: 0.75rem;
  position: relative;
  overflow: hidden;
  height: 48px;
  opacity: ${(props) => (props.$isDisabled ? 0.5 : 1)};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background-color: ${(props) =>
      props.$isActive && !props.$isDisabled ? '#800085' : 'transparent'};
    box-shadow: ${(props) =>
      props.$isActive && !props.$isDisabled ? '0 0 10px rgba(128, 0, 133, 0.5)' : 'none'};
    transition: all 0.2s ease;
  }

  &:hover {
    background-color: ${(props) => (!props.$isDisabled ? 'rgba(128, 0, 133, 0.1)' : 'transparent')};
    transform: ${(props) => (!props.$isDisabled ? 'translateX(2px)' : 'none')};
    box-shadow: ${(props) => (!props.$isDisabled ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none')};
  }

  &:active {
    transform: ${(props) => (!props.$isDisabled ? 'translateX(1px)' : 'none')};
  }
`

const NavItemContainer = styled.div<{ $isActive: boolean }>`
  padding: 0.25rem 0.5rem;
  margin: 0.25rem 0;
  width: 100%;

  ${NavItem} {
    height: 100%;
  }
`

const NavItemText = styled.span<{ $isActive: boolean; $isCollapsed: boolean }>`
  white-space: nowrap;
  overflow: hidden;
  opacity: ${(props) => (props.$isCollapsed ? '0' : '1')};
  visibility: ${(props) => (props.$isCollapsed ? 'hidden' : 'visible')};
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${(props) => (props.$isCollapsed ? '0s' : '0.1s')};
  font-weight: ${(props) => (props.$isActive ? '500' : '400')};
`

const IconWrapper = styled.div<{ $isActive: boolean; $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.$isActive ? '#fff' : '#888')};
  transition: all 0.2s ease;
  padding: 0.5rem;
  min-width: 44px;
  height: 44px;
  border-radius: 8px;
  background: ${(props) => (props.$isActive ? 'rgba(128, 0, 133, 0.3)' : 'transparent')};
  box-shadow: ${(props) => (props.$isActive ? '0 0 15px rgba(128, 0, 133, 0.3)' : 'none')};

  ${NavItem}:hover & {
    color: #fff;
    transform: scale(1.05);
    background: rgba(128, 0, 133, 0.2);
  }

  button {
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    margin: 0;
    padding: 0;
    width: 24px;
    height: 24px;
  }
`

const SectionHeader = styled.div<{ $isCollapsed: boolean }>`
  position: absolute;
  top: 2rem;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${(props) => (props.$isCollapsed ? '1rem' : '1.5rem')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  transform: ${(props) => (props.$isCollapsed ? 'translateY(0)' : 'scale(1)')};
  pointer-events: none;
`

const SectionIcon = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: ${(props) => (props.$isCollapsed ? '0' : '0.5rem')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${(props) => (props.$isCollapsed ? 'scale(1)' : 'scale(1) translateY(0)')};

  svg {
    width: ${(props) => (props.$isCollapsed ? '36px' : '48px')};
    height: ${(props) => (props.$isCollapsed ? '36px' : '48px')};
    filter: drop-shadow(0 2px 8px rgba(128, 0, 133, 0.4));
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`

const SectionTitle = styled.h2<{ $isCollapsed: boolean }>`
  color: white;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
  text-align: center;
  opacity: ${(props) => (props.$isCollapsed ? '0' : '1')};
  transform: ${(props) => (props.$isCollapsed ? 'translateY(10px)' : 'translateY(0)')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  height: ${(props) => (props.$isCollapsed ? '0' : 'auto')};
  overflow: hidden;
`

const getIconForDatasetItem = (item: string, isCollapsed: boolean, isActive: boolean) => {
  const size = 24
  switch (item) {
    case 'Overview':
      return <LayoutDashboard size={size} />
    case 'Labeling':
      return <FileSpreadsheet size={size} />
    case 'Tags':
      return <Tags size={size} />
    case 'Content':
      return <Images size={size} />
    default:
      return <LayoutDashboard size={size} />
  }
}

const getIconForInferenceItem = (item: string, isCollapsed: boolean, isActive: boolean) => {
  const size = 24
  switch (item) {
    case 'Overview':
      return <LayoutDashboard size={size} />
    case 'Adaptive Active Labeling':
      return <Repeat2 size={size} />
    case 'Real Time':
      return <BarChart3 size={size} />
    case 'Map (Beta)':
      return <MapPin size={size} />
    default:
      return <Zap size={size} />
  }
}

const getIconForModelItem = (item: string, isCollapsed: boolean, isActive: boolean) => {
  const size = 24
  switch (item) {
    case 'Overview':
      return <LayoutDashboard size={size} />
    case 'Training':
      return <Zap size={size} />
    case 'Performance':
      return <Gauge size={size} />
    case 'Deployment':
      return <Rocket size={size} />
    case 'Compare':
      return <ArrowLeftRight size={size} />
    case 'Model Datasets':
      return <FileSpreadsheet size={size} />
    case 'Versions':
      return <GitBranch size={size} />
    default:
      return <LayoutDashboard size={size} />
  }
}

const getIconForDashboardItem = (item: string, isCollapsed: boolean, isActive: boolean) => {
  const size = 24
  switch (item) {
    case 'Overview':
      return <Building2 size={size} />
    default:
      return <LayoutDashboard size={size} />
  }
}

const getSectionIcon = (section: string, isCollapsed: boolean) => {
  const strokeWidth = isCollapsed ? 1.25 : 1.5
  switch (section) {
    case 'datasets':
      return <Database strokeWidth={strokeWidth} />
    case 'models':
      return <Brain strokeWidth={strokeWidth} />
    case 'inference':
      return <Zap strokeWidth={strokeWidth} />
    case 'dashboard':
      return <LayoutDashboard strokeWidth={strokeWidth} />
    default:
      return <Wrench strokeWidth={strokeWidth} />
  }
}

const getSectionTitle = (section: string) => {
  switch (section) {
    case 'datasets':
      return 'Datasets'
    case 'models':
      return 'Models'
    case 'inference':
      return 'Inference'
    case 'dashboard':
      return 'Dashboard'
    default:
      return 'Tools'
  }
}

const CollapsibleNav: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const navRef = useRef<HTMLDivElement>(null)
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const activeDatasetSubView = useAppSelector(selectActiveDatasetSubView)
  const activeInferenceSubView = useAppSelector(selectActiveInferenceSubView)
  const activeModelSubView = useAppSelector(selectActiveModelSubView)
  const activeDashboardSubView = useAppSelector(selectActiveDashboardSubView)

  // Fetch project data to get the project type
  const { data: projectData } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  // Fetch models for the current project
  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  // Fetch selected model data
  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const models = modelsData?.models || []
  const hasInitializedModels = models.length > 0
  const hasTrainedModels = models.some((model) => model.state === 'TRAINED')
  const hasMultipleModels = models.length > 1

  const currentSection = location.pathname.split('/')[1]

  // Add effect to handle model deployment status changes
  useEffect(() => {
    // Check if we're on the realtime page and model is not in service
    if (
      location.pathname.includes('/inference') &&
      location.pathname.includes('/realtime') &&
      selectedModel?.deployment?.status !== 'IN_SERVICE'
    ) {
      // Redirect to overview
      navigate(`/inference/${selectedProjectId}/overview`)
      dispatch(setActiveInferenceSubView('Overview'))
    }
  }, [selectedModel?.deployment?.status, location.pathname, navigate, selectedProjectId, dispatch])

  useEffect(() => {
    const pathSegments = location.pathname.split('/')
    const mainSection = pathSegments[1]
    const subSection = pathSegments[3]

    if (mainSection === 'datasets') {
      dispatch(
        setActiveDatasetSubView(
          subSection === 'overview' ? 'Overview' : (subSection as DatasetSubView)
        )
      )
    } else if (mainSection === 'models') {
      const routeToViewMap: { [key: string]: ModelSubView } = {
        overview: 'Overview',
        training: 'Training',
        performance: 'Performance',
        deployment: 'Deployment',
        compare: 'Compare',
        datasets: 'Model Datasets',
        versions: 'Versions',
      }
      dispatch(setActiveModelSubView(routeToViewMap[subSection] ?? 'Overview'))
    } else if (mainSection === 'inference') {
      if (subSection === 'adaptive-active-labeling') {
        dispatch(setActiveInferenceSubView('Adaptive Active Labeling'))
      } else if (subSection === 'realtime') {
        dispatch(setActiveInferenceSubView('Real Time'))
      } else if (subSection === 'map') {
        dispatch(setActiveInferenceSubView('Map (Beta)'))
      } else if (subSection === 'synthetic-data') {
        dispatch(setActiveInferenceSubView('Synthetic Data'))
      } else {
        dispatch(setActiveInferenceSubView('Overview'))
      }
    }
  }, [location.pathname, dispatch, navigate, selectedProjectId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isCollapsed && navRef.current && !navRef.current.contains(event.target as Node)) {
        setIsCollapsed(true)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCollapsed])

  const handleDatasetSubNavSelect = (item: DatasetSubView) => {
    dispatch(setActiveDatasetSubView(item))
    if (item === 'Overview') {
      navigate(`/datasets/${selectedProjectId}/overview`)
    } else if (item === 'Labeling') {
      navigate(`/datasets/${selectedProjectId}/labeling`)
    } else if (item === 'Tags') {
      navigate(`/datasets/${selectedProjectId}/tags`)
    } else if (item === 'Content') {
      navigate(`/datasets/${selectedProjectId}/content`)
    }
    setIsCollapsed(true)
  }

  const handleInferenceSubNavSelect = (item: InferenceSubView) => {
    dispatch(setActiveInferenceSubView(item))
    if (item === 'Overview') {
      navigate(`/inference/${selectedProjectId}/overview`)
    } else if (item === 'Adaptive Active Labeling') {
      navigate(`/inference/${selectedProjectId}/adaptive-active-labeling`)
    } else if (item === 'Real Time') {
      navigate(`/inference/${selectedProjectId}/realtime`)
    } else if (item === 'Map (Beta)') {
      navigate(`/inference/${selectedProjectId}/map`)
    } else if (item === 'Synthetic Data') {
      navigate(`/inference/${selectedProjectId}/synthetic-data`)
    }
    setIsCollapsed(true)
  }

  const handleModelSubNavSelect = (item: ModelSubView) => {
    dispatch(setActiveModelSubView(item))
    if (item === 'Overview') {
      navigate(`/models/${selectedProjectId}/overview`)
    } else if (item === 'Training') {
      navigate(`/models/${selectedProjectId}/training`)
    } else if (item === 'Performance') {
      navigate(`/models/${selectedProjectId}/performance`)
    } else if (item === 'Deployment') {
      navigate(`/models/${selectedProjectId}/deployment`)
    } else if (item === 'Compare') {
      navigate(`/models/${selectedProjectId}/compare`)
    } else if (item === 'Model Datasets') {
      navigate(`/models/${selectedProjectId}/datasets`)
    } else if (item === 'Versions') {
      navigate(`/models/${selectedProjectId}/versions`)
    }
    setIsCollapsed(true)
  }

  const handleDashboardSubNavSelect = (item: DashboardSubView) => {
    dispatch(setActiveDashboardSubView(item))
    navigate('/dashboard')
    setIsCollapsed(true)
  }

  const renderDashboardNav = () => (
    <NavSection>
      {DashboardSubNavItems.map((item, index) => {
        const isActive = activeDashboardSubView === item

        return (
          <React.Fragment key={item}>
            <OverlayTrigger
              placement="right"
              overlay={(props) => (
                <Tooltip id={`tooltip-${item}`} {...props}>
                  {item.replace('-', ' ')}
                </Tooltip>
              )}
            >
              <NavItemContainer $isActive={isActive}>
                <NavItem
                  $isActive={isActive}
                  onClick={() => handleDashboardSubNavSelect(item)}
                  $isCollapsed={isCollapsed}
                >
                  <IconWrapper $isActive={isActive} $isCollapsed={isCollapsed}>
                    {getIconForDashboardItem(item, isCollapsed, isActive)}
                  </IconWrapper>
                  <NavItemText $isActive={isActive} $isCollapsed={isCollapsed}>
                    {item.replace('-', ' ')}
                  </NavItemText>
                </NavItem>
              </NavItemContainer>
            </OverlayTrigger>
          </React.Fragment>
        )
      })}
    </NavSection>
  )

  const renderDatasetNav = () => (
    <NavSection>
      <>
        {DatasetSubNavItems.map((item, index) => {
          const isActive = activeDatasetSubView.toLowerCase() === item.toLowerCase()
          // Find the index of Content item
          const contentIndex = DatasetSubNavItems.findIndex((i) => i === 'Content')
          // Add divider after Overview or Content
          const shouldShowDivider = item === 'Overview' || index === contentIndex

          return (
            <React.Fragment key={item}>
              <OverlayTrigger
                placement="right"
                overlay={(props) => (
                  <Tooltip id={`tooltip-${item}`} {...props}>
                    {item.replace('-', ' ')}
                  </Tooltip>
                )}
              >
                <NavItemContainer $isActive={isActive}>
                  <NavItem
                    $isActive={isActive}
                    onClick={() => handleDatasetSubNavSelect(item)}
                    $isCollapsed={isCollapsed}
                  >
                    <IconWrapper $isActive={isActive} $isCollapsed={isCollapsed}>
                      {getIconForDatasetItem(item, isCollapsed, isActive)}
                    </IconWrapper>
                    <NavItemText $isActive={isActive} $isCollapsed={isCollapsed}>
                      {item.replace('-', ' ')}
                    </NavItemText>
                  </NavItem>
                </NavItemContainer>
              </OverlayTrigger>
              {shouldShowDivider && <Divider $isCollapsed={isCollapsed} />}
            </React.Fragment>
          )
        })}
      </>
    </NavSection>
  )

  const renderInferenceNav = () => {
    // Filter out Map option for classification projects
    const filteredNavItems = InferenceSubNavItems.filter(
      (item) => !(item === 'Map (Beta)' && projectData?.annotation_type === 'classification')
    )

    return (
      <NavSection>
        {filteredNavItems.map((item, index) => {
          const isActive = activeInferenceSubView === item
          // Group by: Overview | Active Features (AAL, Real Time) | Experimental Features
          const shouldShowDivider = item === 'Overview' || item === 'Real Time'

          // Determine if the nav item should be disabled
          const isDisabled = (() => {
            if (item === 'Real Time') {
              return selectedModel?.deployment?.status !== 'IN_SERVICE'
            }
            return false
          })()

          return (
            <React.Fragment key={item}>
              <OverlayTrigger
                placement="right"
                overlay={(props) => (
                  <Tooltip id={`tooltip-${item}`} {...props}>
                    {isDisabled && item === 'Real Time'
                      ? 'Real Time (Model must be deployed and in service)'
                      : item.replace('-', ' ')}
                  </Tooltip>
                )}
              >
                <NavItemContainer $isActive={isActive}>
                  <NavItem
                    $isActive={isActive}
                    onClick={() => !isDisabled && handleInferenceSubNavSelect(item)}
                    $isCollapsed={isCollapsed}
                    $isDisabled={isDisabled}
                  >
                    <IconWrapper $isActive={isActive} $isCollapsed={isCollapsed}>
                      {getIconForInferenceItem(item, isCollapsed, isActive)}
                    </IconWrapper>
                    <NavItemText $isActive={isActive} $isCollapsed={isCollapsed}>
                      {item.replace('-', ' ')}
                    </NavItemText>
                  </NavItem>
                </NavItemContainer>
              </OverlayTrigger>
              {shouldShowDivider && <Divider $isCollapsed={isCollapsed} />}
            </React.Fragment>
          )
        })}
      </NavSection>
    )
  }

  const renderModelNav = () => (
    <NavSection>
      {ModelSubNavItems.map((item, index) => {
        const isActive = activeModelSubView.toLocaleLowerCase() === item.toLocaleLowerCase()
        // Group by: Overview | Development (Training, Performance) | Deployment | Analysis (Compare, Model Datasets)
        const shouldShowDivider =
          item === 'Overview' || item === 'Performance' || item === 'Deployment'

        // Determine if the nav item should be disabled
        const isDisabled = (() => {
          if (item === 'Overview') return false
          if (!hasInitializedModels) return true
          if (item === 'Training') return false
          if (['Performance', 'Deployment'].includes(item)) {
            // Disable if no trained models OR if the selected model is in TRAINING status
            return (
              !hasTrainedModels ||
              selectedModel?.state.toLowerCase() === 'training' ||
              selectedModel?.state.toLowerCase() === 'initialized'
            )
          }
          if (item === 'Compare') return !hasMultipleModels
          return false
        })()

        return (
          <React.Fragment key={item}>
            <OverlayTrigger
              placement="right"
              overlay={(props) => (
                <Tooltip id={`tooltip-${item}`} {...props}>
                  {isDisabled
                    ? `${item} (${
                        !hasInitializedModels
                          ? 'No models available'
                          : !hasTrainedModels
                          ? 'No trained models'
                          : selectedModel?.state.toLowerCase() === 'training' ||
                            selectedModel?.state.toLowerCase() === 'initialized'
                          ? 'Model is not trained'
                          : 'Need at least 2 models'
                      })`
                    : item.replace('-', ' ')}
                </Tooltip>
              )}
            >
              <NavItemContainer $isActive={isActive}>
                <NavItem
                  $isActive={isActive}
                  onClick={() => !isDisabled && handleModelSubNavSelect(item)}
                  $isCollapsed={isCollapsed}
                  $isDisabled={isDisabled}
                >
                  <IconWrapper $isActive={isActive} $isCollapsed={isCollapsed}>
                    {getIconForModelItem(item, isCollapsed, isActive)}
                  </IconWrapper>
                  <NavItemText $isActive={isActive} $isCollapsed={isCollapsed}>
                    {item.replace('-', ' ')}
                  </NavItemText>
                </NavItem>
              </NavItemContainer>
            </OverlayTrigger>
            {shouldShowDivider && <Divider $isCollapsed={isCollapsed} />}
          </React.Fragment>
        )
      })}
    </NavSection>
  )

  return (
    <NavContainer ref={navRef} $isCollapsed={isCollapsed}>
      <SectionHeader $isCollapsed={isCollapsed}>
        <SectionIcon $isCollapsed={isCollapsed}>
          {getSectionIcon(currentSection, isCollapsed)}
        </SectionIcon>
        <SectionTitle $isCollapsed={isCollapsed}>{getSectionTitle(currentSection)}</SectionTitle>
      </SectionHeader>
      <ToggleButton onClick={() => setIsCollapsed(!isCollapsed)}>
        {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
      </ToggleButton>
      <NavContent>
        {location.pathname.split('/')[1] === 'dashboard' && renderDashboardNav()}
        {location.pathname.split('/')[1] === 'datasets' && renderDatasetNav()}
        {location.pathname.split('/')[1] === 'models' && renderModelNav()}
        {location.pathname.split('/')[1] === 'inference' && renderInferenceNav()}
      </NavContent>
    </NavContainer>
  )
}

export default CollapsibleNav
